/**
 * «Cuentas de servicio» — the keys of ASGTRANS's own systems (#161; Addendum No. 3, Point 5; spec
 * `docs/superpowers/specs/2026-10-09-deca-service-accounts-161-design.md` §9).
 *
 * A platform super administrator's screen, in ASGTRANS's own view only: the server refuses one acting inside
 * a client company, so with a company chosen the screen says where to go rather than failing. The key is
 * shown once, as a company key is on «Claves de API», and the dialog says before the name is typed that client
 * companies see it in their audit trail, beside every answered query that names them by NIF (a query for all
 * customers, and one refused, are filed in ASGTRANS's trail alone).
 *
 * Focus goes to the key when it appears, and closing the dialog while it shows asks first: it is the only time
 * the key can be read.
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { unwrap, type ServiceAccount } from '@deca/api'

import { api } from '../app/api.ts'
import { apiErrorMessage } from '../app/errors.ts'
import { qk, useServiceAccounts } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { formatDateTime } from '../deca/format.ts'
import { AdminShell } from './AdminShell.tsx'
import { Button, Card, Chip, CopyButton, NoAccessState, Notice } from '../ui/primitives.tsx'
import { DataTable, type Column } from '../ui/DataTable.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { TextInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'

export function AdminServiceAccountsScreen() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const client = useQueryClient()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const { organisation: context } = useOrganisationChoice()

  const list = useServiceAccounts(abilities.platformAdmin && !context)
  const [createOpen, setCreateOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!abilities.platformAdmin) {
    return (
      <AdminShell>
        <Card>
          <NoAccessState />
        </Card>
      </AdminShell>
    )
  }

  if (context) {
    return (
      <AdminShell heading={t('admin.serviceAccounts.title')}>
        {/* The control is named from the catalogue entry that labels it in the company selector, so the two cannot drift. */}
        <Notice tone="info">
          {t('admin.serviceAccounts.leaveCompany', { organisation: context.name, leave: t('organisation.clear') })}
        </Notice>
      </AdminShell>
    )
  }

  async function revoke(account: ServiceAccount) {
    if (!window.confirm(t('admin.serviceAccounts.revokeConfirm', { name: account.name }))) return
    setError(null)
    try {
      await unwrap(
        api.DELETE('/api/v1/service-accounts/{accountId}', { params: { path: { accountId: account.id } } }),
      )
      void client.invalidateQueries({ queryKey: qk.serviceAccounts() })
      toast.success(t('admin.serviceAccounts.revoked'))
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    }
  }

  const columns: Column<ServiceAccount>[] = [
    {
      key: 'name',
      header: t('admin.serviceAccounts.name'),
      render: (row) => (
        <div className="stack-sm" style={{ gap: 0 }}>
          <span style={{ fontWeight: 600 }}>{row.name}</span>
          <span className="mono muted">{row.prefix}</span>
        </div>
      ),
    },
    {
      key: 'created',
      header: t('admin.serviceAccounts.created'),
      render: (row) => (
        <div className="stack-sm" style={{ gap: 0 }}>
          <span>{formatDateTime(row.createdAt, i18n.language)}</span>
          {row.createdBy ? <span className="small muted">{row.createdBy.display}</span> : null}
        </div>
      ),
    },
    {
      key: 'lastUsed',
      header: t('admin.serviceAccounts.lastUsed'),
      numeric: true,
      render: (row) => (row.lastUsedAt ? formatDateTime(row.lastUsedAt, i18n.language) : t('common.never')),
    },
    {
      key: 'status',
      header: t('admin.serviceAccounts.status'),
      card: 'end',
      render: (row) =>
        row.revokedAt ? (
          <Chip tone="danger" icon="ban" muted>
            {t('admin.serviceAccounts.revokedAt')}
          </Chip>
        ) : (
          <Chip tone="success" icon="checkCircle">
            {t('admin.serviceAccounts.active')}
          </Chip>
        ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      render: (row) =>
        row.revokedAt ? null : (
          <div className="table__actions">
            {/* Named for its account: the visible word is the same on every row, and a list of buttons that all say
                «Revocar» cannot be told apart by someone who hears them one at a time. */}
            <Button
              size="sm"
              variant="ghost"
              icon="ban"
              aria-label={t('admin.serviceAccounts.revokeNamed', { name: row.name })}
              onClick={() => void revoke(row)}
            >
              {t('admin.serviceAccounts.revoke')}
            </Button>
          </div>
        ),
    },
  ]

  return (
    <AdminShell heading={t('admin.serviceAccounts.title')} subtitle={t('admin.serviceAccounts.subtitle')}>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card
        flush
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>
            {t('admin.serviceAccounts.create')}
          </Button>
        }
      >
        <DataTable
          rows={list.data?.data}
          columns={columns}
          rowKey={(row) => row.id}
          caption={t('admin.serviceAccounts.title')}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      </Card>

      <CreateServiceAccountDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void client.invalidateQueries({ queryKey: qk.serviceAccounts() })}
      />
    </AdminShell>
  )
}

function CreateServiceAccountDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [secret, setSecret] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const reveal = useRef<HTMLDivElement>(null)

  // The field and «Crear» that had focus are replaced by the key in the same render, and focus would fall to the
  // page behind the dialog: Tab would leave the modal, Enter on a sidebar link would navigate away and lose the
  // key, and a screen reader would say nothing about a key that is shown once. Move it to the key.
  useEffect(() => {
    if (secret) reveal.current?.focus()
  }, [secret])

  function close() {
    setName('')
    setSecret(null)
    setError(null)
    onClose()
  }

  /**
   * ✕, Escape and a click on the backdrop. While the key is on screen each would throw it away unread, from one
   * stray click, so they ask first. «La he copiado» is the user saying it has been copied and closes directly.
   */
  function dismiss() {
    if (secret && !window.confirm(t('admin.serviceAccounts.secretDiscardConfirm'))) return
    close()
  }

  async function submit() {
    if (!name.trim()) {
      setError(t('errors.required'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const created = await unwrap(api.POST('/api/v1/service-accounts', { body: { name: name.trim() } }))
      setSecret(created.secret)
      onCreated()
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={secret ? t('admin.serviceAccounts.secretTitle') : t('admin.serviceAccounts.create')}
      onClose={dismiss}
      footer={
        secret ? (
          <Button variant="primary" icon="check" onClick={close}>
            {t('admin.serviceAccounts.secretAcknowledge')}
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" loading={busy} disabled={busy} onClick={() => void submit()}>
              {t('common.create')}
            </Button>
          </>
        )
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      {secret ? (
        // `stack-lg` is the dialog body's own gap, so the two keep the spacing they had as its direct children.
        <div className="stack-lg" ref={reveal} tabIndex={-1}>
          <Notice tone="warning" title={t('admin.serviceAccounts.secretTitle')}>
            {t('admin.serviceAccounts.secretHelp')}
          </Notice>
          <div className="secret-reveal">
            <code>{secret}</code>
            <CopyButton value={secret} />
          </div>
        </div>
      ) : (
        <>
          <Notice tone="info">{t('admin.serviceAccounts.nameShown')}</Notice>
          <TextInput
            label={t('admin.serviceAccounts.name')}
            placeholder={t('admin.serviceAccounts.namePlaceholder')}
            required
            maxLength={80}
            value={name}
            onChange={(event) => setName(event.target.value)}
            // The dialog has no `<form>`, so Enter creates only through this, and never while a create is on its way.
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !busy) void submit()
            }}
          />
        </>
      )}
    </Dialog>
  )
}
